import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { client } from '../api/client';
import { getRefCode } from '../lib/referral';
import { normalizeHKPhone } from '../lib/phone';

export default function InvestPage() {
  const { t } = useTranslation('plan');
  const [form, setForm] = useState({ name: '', email: '', phone: '+852 ', vibe: '', property: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const res = await client.api.fetch('/api/public/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: normalizeHKPhone(form.phone),
          lead_type: 'island_owner_talk',
          source: 'plan_page',
          referral_code: getRefCode(),
          metadata: {
            vibe: form.vibe,
            property: form.property,
            message: form.message,
          },
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        console.error('Lead submit failed:', await res.text());
      }
    } catch (err) {
      console.error('Lead submit error:', err);
    }
  }

  return (
    <div className="pt-16">
      {/* Hero — Desktop */}
      <section className="relative hidden md:block h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1688949078626-a358f500e063?w=1920&q=80"
            alt="Maldives private island"
            className="w-full h-full object-cover object-[70%_center]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a4c6b]/95 via-[#0a4c6b]/40 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex items-end px-6 lg:px-16 pb-16 lg:pb-24">
          <div className="max-w-2xl text-left text-white">
            <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">{t('hero.eyebrow')}</p>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">{t('hero.title')}</h1>
            <p className="text-lg lg:text-xl text-white/90 max-w-xl mb-8">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-start">
              <Link
                to="/properties"
                className="bg-white text-[#0a4c6b] px-8 py-3 rounded-full font-semibold hover:bg-white/90 transition"
              >
                {t('hero.cta.properties')}
              </Link>
              <a
                href="#consultation"
                className="bg-[#B8902F] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#9a7a28] transition"
              >
                {t('hero.cta.consult')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Hero — Mobile */}
      <section className="md:hidden min-h-screen flex flex-col bg-[#0d1b2a]">
        <div className="h-[58vh] w-full relative">
          <img
            src="https://images.unsplash.com/photo-1688949078626-a358f500e063?w=1920&q=80"
            alt="Maldives private island"
            className="w-full h-full object-cover object-[50%_35%]"
          />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-10 text-center text-white">
          <div className="max-w-xl">
            <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">{t('hero.eyebrow')}</p>
            <h1 className="text-4xl font-bold mb-6">{t('hero.title')}</h1>
            <p className="text-lg text-white/90 mb-8">
              {t('hero.description')}
            </p>
            <div className="flex flex-col gap-4">
              <Link
                to="/properties"
                className="bg-white text-[#0a4c6b] px-8 py-3 rounded-full font-semibold hover:bg-white/90 transition"
              >
                {t('hero.cta.properties')}
              </Link>
              <a
                href="#consultation"
                className="bg-[#B8902F] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#9a7a28] transition"
              >
                {t('hero.cta.consult')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Authority Bar */}
      <section className="bg-[#0d1b2a] py-6 px-4 border-b border-white/10">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
          <div>
            <p className="text-2xl font-bold text-[#B8902F]">{t('authorityBar.bookings.value')}</p>
            <p className="text-sm text-white/70">{t('authorityBar.bookings.label')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#B8902F]">{t('authorityBar.experience.value')}</p>
            <p className="text-sm text-white/70">{t('authorityBar.experience.label')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#B8902F]">{t('authorityBar.resort.value')}</p>
            <p className="text-sm text-white/70">{t('authorityBar.resort.label')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#B8902F]">{t('authorityBar.yearRound.value')}</p>
            <p className="text-sm text-white/70">{t('authorityBar.yearRound.label')}</p>
          </div>
        </div>
      </section>

      {/* Rental Proof / Demand Evidence */}
      <section className="bg-[#B8902F] py-10 px-4">
        <div className="max-w-5xl mx-auto text-center text-white">
          <p className="text-white/80 text-sm tracking-widest uppercase mb-4">{t('proof.eyebrow')}</p>
          <h2 className="text-2xl md:text-3xl font-bold mb-8">{t('proof.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <p className="text-4xl md:text-5xl font-bold mb-2">{t('proof.occupancy.value')}</p>
              <p className="text-white/90 text-sm">{t('proof.occupancy.label')}</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold mb-2">{t('proof.bookings.value')}</p>
              <p className="text-white/90 text-sm">{t('proof.bookings.label')}</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-bold mb-2">{t('proof.rating.value')}</p>
              <p className="text-white/90 text-sm">{t('proof.rating.label')}</p>
            </div>
          </div>
          <p className="mt-8 text-white/80 text-sm max-w-2xl mx-auto">
            {t('proof.footnote')}
          </p>
        </div>
      </section>

      {/* Why Invest */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#2ec4b6] font-medium mb-2">{t('whyUs.eyebrow')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2a]">{t('whyUs.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(t('whyUs.reasons', { returnObjects: true }) as Array<{ icon: string; title: string; desc: string }>).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 rounded-2xl border border-gray-100 hover:border-[#0a4c6b]/20 hover:shadow-lg transition"
              >
                <span className="text-4xl mb-4 block">{item.icon}</span>
                <h3 className="font-bold text-xl mb-3 text-[#0d1b2a]">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 px-4 bg-[#f8fafb]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#B8902F] font-medium mb-2">{t('steps.eyebrow')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2a]">{t('steps.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {(t('steps.items', { returnObjects: true }) as Array<{ step: string; title: string; desc: string }>).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl font-bold text-[#0a4c6b]/20 mb-4">{item.step}</p>
                <h3 className="font-bold text-lg text-[#0d1b2a] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="consultation" className="py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-[#B8902F] font-medium mb-2">{t('consultation.eyebrow')}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2a] mb-6">{t('consultation.title')}</h2>
              <p className="text-gray-600 mb-8">
                {t('consultation.description')}
              </p>
              <div className="bg-[#f8fafb] rounded-2xl p-6 border border-gray-100 mb-8">
                <h3 className="font-bold text-[#0d1b2a] mb-4">{t('consultation.topics.title')}</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  {(t('consultation.topics.items', { returnObjects: true }) as string[]).map((topic, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="text-[#2ec4b6]">✓</span> {topic}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#2ec4b6] text-white flex items-center justify-center text-2xl mx-auto mb-4">✓</div>
                  <h3 className="text-2xl font-bold text-[#0d1b2a] mb-2">{t('consultation.success.title')}</h3>
                  <p className="text-gray-600">{t('consultation.success.description')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('consultation.form.name.label')}</label>
                    <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]" placeholder={t('consultation.form.name.placeholder')} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('consultation.form.email.label')}</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]" placeholder={t('consultation.form.email.placeholder')} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('consultation.form.phone.label')}</label>
                    <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]" placeholder={t('consultation.form.phone.placeholder')} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('consultation.form.vibe.label')}</label>
                    <select value={form.vibe} onChange={(e) => setForm({ ...form, vibe: e.target.value })} className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]">
                      <option value="">{t('consultation.form.vibe.placeholder')}</option>
                      <option value="private">{t('consultation.form.vibe.options.private')}</option>
                      <option value="active">{t('consultation.form.vibe.options.active')}</option>
                      <option value="quiet">{t('consultation.form.vibe.options.quiet')}</option>
                      <option value="family">{t('consultation.form.vibe.options.family')}</option>
                      <option value="undecided">{t('consultation.form.vibe.options.undecided')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('consultation.form.property.label')}</label>
                    <select value={form.property} onChange={(e) => setForm({ ...form, property: e.target.value })} className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]">
                      <option value="">{t('consultation.form.property.placeholder')}</option>
                      <option value="mikado">{t('consultation.form.property.options.mikado')}</option>
                      <option value="private-island">{t('consultation.form.property.options.privateIsland')}</option>
                      <option value="madivaru">{t('consultation.form.property.options.madivaru')}</option>
                      <option value="undecided">{t('consultation.form.property.options.undecided')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('consultation.form.message.label')}</label>
                    <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]" placeholder={t('consultation.form.message.placeholder')} />
                  </div>
                  <button type="submit" className="w-full bg-[#B8902F] text-white py-3 rounded-xl font-semibold hover:bg-[#9a7a28] transition">
                    {t('consultation.form.submit')}
                  </button>
                  <p className="text-xs text-gray-500 text-center">{t('consultation.form.privacy')}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 bg-[#f8fafb]">
        <div className="max-w-4xl mx-auto text-center space-y-3">
          <p className="text-xs text-gray-500">
            {t('disclaimer')}
          </p>
        </div>
      </section>
    </div>
  );
}
